/**
 * Small RFC 4180-ish CSV parser used by the rescue import wizard. Handles:
 *   - Quoted fields with embedded commas / newlines
 *   - Escaped double-quotes ("")
 *   - CRLF, CR, and LF line endings
 *   - Stray blank trailing line at EOF (ignored)
 *
 * Intentionally not a full streaming parser — the import flow caps at
 * 500 rows / 1 MB. For larger feeds switch to PapaParse or similar.
 *
 * Throws `CsvParseError` on malformed input (currently: unclosed quoted
 * field at EOF). Returns rows as `string[][]`. Trimming is the caller's
 * responsibility — the parser preserves whitespace.
 */

export class CsvParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CsvParseError';
  }
}

export function parseCsv(text: string): string[][] {
  // Strip a leading UTF-8 BOM (U+FEFF) — Excel saves CSVs with one by
  // default, and without this it would survive into the first header
  // cell, breaking column-name matching with a cryptic "unknown
  // column" error that's visually identical to the valid header name.
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = '';
  let inQuotes = false;
  // RFC 4180: a quoted field must wrap the ENTIRE cell. Track whether the
  // current cell started with a quote so we can reject bare quotes inside
  // an unquoted cell (e.g. `a"b,c"d`).
  let cellStartedWithQuote = false;
  // True the moment a cell starts (after a delimiter / newline / BOF) and
  // before any non-quote character is written. Lets us distinguish a
  // leading `"` (opens a quoted region) from a `"` mid-cell (malformed).
  let atCellStart = true;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      if (!atCellStart) {
        throw new CsvParseError('CSV has a bare quote inside an unquoted field');
      }
      inQuotes = true;
      cellStartedWithQuote = true;
      atCellStart = false;
    } else if (ch === ',') {
      cur.push(field);
      field = '';
      cellStartedWithQuote = false;
      atCellStart = true;
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') {
        i++;
      }
      cur.push(field);
      field = '';
      rows.push(cur);
      cur = [];
      cellStartedWithQuote = false;
      atCellStart = true;
    } else {
      // A quoted region closed and was followed by more text in the same
      // cell (e.g. `"abc"def`). RFC 4180 forbids this.
      if (cellStartedWithQuote) {
        throw new CsvParseError('CSV has data after a closing quote in the same field');
      }
      field += ch;
      atCellStart = false;
    }
  }
  // EOF inside a quoted field means the user's CSV is malformed (e.g.
  // they dropped a closing quote). We refuse to silently swallow it —
  // that surfaces as "name is required" downstream and confuses users.
  if (inQuotes) {
    throw new CsvParseError('CSV ends with an unclosed quoted field');
  }
  if (field.length > 0 || cur.length > 0) {
    cur.push(field);
    rows.push(cur);
  }
  return rows.filter((r) => r.length > 0 && !(r.length === 1 && r[0] === ''));
}
